import React, { useState } from "react";
import { BadgeCheck, AlertTriangle, Shirt, ChevronDown, ChevronUp, ClipboardList, Ban, Search, Database } from "lucide-react";

/* Badge di qualifica della pipeline unica Rivenditori (qualificato ≥70 / da_verificare / legacy). */
export const QualBadge = ({ lead, testid = "result-qual-badge" }) => {
  const q = lead?.qualification;
  if (!q) return null;
  const base = "mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold";
  if (q === "qualificato") {
    return (
      <span data-testid={testid} className={`${base} bg-positive/15 text-positive`}>
        <BadgeCheck className="h-3 w-3" /> Qualificato ≥70{lead.business_type_label ? ` · ${lead.business_type_label}` : ""}
      </span>
    );
  }
  if (q === "da_verificare") {
    return (
      <span data-testid={testid} className={`${base} bg-amber-500/15 text-amber-600 dark:text-amber-400`}>
        <AlertTriangle className="h-3 w-3" /> Da verificare{lead.contact_missing ? " · contatto mancante" : ""}
      </span>
    );
  }
  if (q === "brand") {
    return (
      <span data-testid={testid} className={`${base} bg-positive/15 text-positive`}>
        <BadgeCheck className="h-3 w-3" /> Brand di riferimento{Array.isArray(lead.brands) && lead.brands.length ? `: ${lead.brands.slice(0, 3).join(", ")}` : ""}
      </span>
    );
  }
  if (q === "apparel") {
    return (
      <span data-testid={testid} className={`${base} bg-brand/15 text-brand`}>
        <Shirt className="h-3 w-3" /> Reparto apparel
      </span>
    );
  }
  return null;
};

/* Ordine risultati: qualificati prima, "Da verificare" sempre in coda. */
export const qualRank = (lead) => (lead?.qualification === "da_verificare" ? 1 : 0);

const Stat = ({ label, value, tone = "", testid }) => (
  <div className="rounded-lg border border-border bg-brema-bg px-3 py-2" data-testid={testid}>
    <div className={`text-lg font-semibold leading-none ${tone}`}>{value ?? 0}</div>
    <div className="mt-1 text-[11px] text-muted-foreground">{label}</div>
  </div>
);

/* Riepilogo dettagliato dell'ultima ricerca (candidati, analizzati, esclusi con motivo, da verificare, qualificati, query, KB). */
export const DiscoveryReportPanel = ({ report, compact = false, onClose }) => {
  const [open, setOpen] = useState(!compact);
  const [showExcluded, setShowExcluded] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  if (!report) return null;
  const kb = report.kb_applied || {};
  return (
    <div className="rounded-xl border border-border bg-brema-card p-3" data-testid="discovery-report">
      <div className="flex flex-wrap items-center gap-2">
        <ClipboardList className="h-4 w-4 text-brand" />
        <span className="text-sm font-semibold text-foreground">Riepilogo ricerca</span>
        <span className="text-xs text-muted-foreground" data-testid="discovery-report-headline">
          {report.qualified ?? 0}/{report.requested} qualificati · {report.analyzed ?? 0} analizzati su {report.candidates_found ?? 0} candidati
          {report.limit_reached ? ` · limite ${report.max_analyzed} raggiunto` : report.exhausted ? " · zona esaurita" : ""}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" data-testid="discovery-report-toggle" onClick={() => setOpen((v) => !v)}
            className="rounded-full p-1 text-muted-foreground hover:bg-brema-hover hover:text-foreground">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {onClose && (
            <button type="button" data-testid="discovery-report-close" onClick={onClose}
              className="rounded-full px-2 text-xs text-muted-foreground hover:bg-brema-hover hover:text-foreground">Chiudi</button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <Stat label="Candidati trovati" value={report.candidates_found} testid="report-candidates" />
            <Stat label={`Analizzati (max ${report.max_analyzed ?? 50})`} value={report.analyzed} testid="report-analyzed" />
            <Stat label={`Qualificati ≥${report.threshold ?? 70}`} value={report.qualified} tone="text-positive" testid="report-qualified" />
            <Stat label="Da verificare" value={report.to_verify} tone="text-amber-600 dark:text-amber-400" testid="report-to-verify" />
            <Stat label="Esclusi" value={report.excluded} tone="text-danger" testid="report-excluded" />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground" data-testid="report-kb">
            <Database className="h-3.5 w-3.5" />
            <span>Knowledge Base: Profilo target {kb.target_profile ? <b className="text-positive">applicato</b> : <b className="text-amber-600">non compilato</b>}</span>
            <span>· Regole di esclusione {kb.exclusion_rules ? <b className="text-positive">applicate</b> : <b className="text-amber-600">non compilate</b>}</span>
            <span>· Marchi di riferimento: {(report.reference_brands || []).length}</span>
            <span>· Percorso: <b className="text-foreground">{report.path === "google_places" ? "Google Places + qualificatore Brema" : "Web search + qualificatore Brema"}</b></span>
            {typeof report.duration_s === "number" && <span>· {report.duration_s}s</span>}
          </div>
          {Array.isArray(report.queries) && report.queries.length > 0 && (
            <div className="text-[11px] text-muted-foreground" data-testid="report-queries">
              <span className="inline-flex items-center gap-1 font-medium text-foreground"><Search className="h-3 w-3" /> Query usate ({report.queries_used ?? report.queries.length}):</span>{" "}
              {report.queries.map((q, i) => <span key={i} className="mr-1 inline-block rounded-full bg-muted px-2 py-0.5">{q}</span>)}
            </div>
          )}
          {Array.isArray(report.excluded_chains) && report.excluded_chains.length > 0 && (
            <div className="text-[11px] text-muted-foreground" data-testid="report-chains">
              <span className="inline-flex items-center gap-1 font-medium text-foreground"><Ban className="h-3 w-3" /> Sempre esclusi:</span>{" "}
              {report.excluded_chains.join(", ")}
            </div>
          )}
          {Array.isArray(report.excluded_details) && report.excluded_details.length > 0 && (
            <div>
              <button type="button" data-testid="report-excluded-toggle" onClick={() => setShowExcluded((v) => !v)}
                className="text-xs font-medium text-danger hover:underline">
                {showExcluded ? "Nascondi" : "Mostra"} esclusi e motivi ({report.excluded_details.length})
              </button>
              {showExcluded && (
                <ul className="mt-1.5 space-y-1" data-testid="report-excluded-list">
                  {report.excluded_details.map((e, i) => (
                    <li key={i} className="rounded-lg border border-border bg-brema-bg px-2.5 py-1.5 text-xs">
                      <span className="font-medium text-foreground">{e.name}</span>
                      {e.city && <span className="text-muted-foreground"> · {e.city}</span>}
                      {e.business_type && <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">{e.business_type}</span>}
                      <div className="text-muted-foreground">{e.reason}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {Array.isArray(report.to_verify_details) && report.to_verify_details.length > 0 && (
            <div>
              <button type="button" data-testid="report-verify-toggle" onClick={() => setShowVerify((v) => !v)}
                className="text-xs font-medium text-amber-600 hover:underline dark:text-amber-400">
                {showVerify ? "Nascondi" : "Mostra"} da verificare ({report.to_verify_details.length})
              </button>
              {showVerify && (
                <ul className="mt-1.5 space-y-1" data-testid="report-verify-list">
                  {report.to_verify_details.map((e, i) => (
                    <li key={i} className="rounded-lg border border-border bg-brema-bg px-2.5 py-1.5 text-xs">
                      <span className="font-medium text-foreground">{e.name}</span>
                      {e.city && <span className="text-muted-foreground"> · {e.city}</span>}
                      {typeof e.fit_score === "number" && <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">Fit {e.fit_score}</span>}
                      <div className="text-muted-foreground">{e.reason}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* Blocco "Perché è in target" della pipeline unica: tipologia, offerta apparel, discipline, evidenze, note KB. */
export const QualificationEvidence = ({ lead }) => {
  if (!lead || !lead.qualifier_engine) return null;
  const apparel = { real: "Offerta abbigliamento/accessori confermata", weak: "Solo termini generici (offerta non confermata)", none: "Nessuna offerta abbigliamento rilevata" }[lead.apparel_offer] || null;
  return (
    <div className="space-y-2" data-testid="detail-qualification-evidence">
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {lead.business_type_label && <span className="rounded-full bg-muted px-2 py-0.5 text-foreground" data-testid="detail-business-type">{lead.business_type_label}</span>}
        {apparel && <span className="rounded-full bg-muted px-2 py-0.5 text-foreground">{apparel}</span>}
        {Array.isArray(lead.disciplines) && lead.disciplines.map((d, i) => (
          <span key={i} className="rounded-full bg-brand/10 px-2 py-0.5 uppercase text-brand">{d}</span>
        ))}
      </div>
      {Array.isArray(lead.evidence) && lead.evidence.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-4 text-[12px] text-foreground" data-testid="detail-evidence-list">
          {lead.evidence.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
      {lead.kb_target_notes && <p className="text-[11px] text-muted-foreground">Profilo target (KB): {lead.kb_target_notes}</p>}
      {typeof lead.fit_raw === "number" && typeof lead.fit_score === "number" && lead.fit_score !== lead.fit_raw && (
        <p className="text-[11px] text-muted-foreground" data-testid="detail-fit-breakdown">Punteggio: {lead.fit_raw} (analisi) + {lead.fit_score - lead.fit_raw} bonus marchi di riferimento = {lead.fit_score}/100</p>
      )}
    </div>
  );
};
